import type { parseFilterModel } from '@/lib/createAgGridDatasource';
import type { SortColumnState } from '@/lib/downloadOptions';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import type { UseMutationResult } from '@tanstack/react-query';
import { useCallback, type RefObject } from 'react';

/** The subset of AG Grid's `GridApi` that `useCsvDownloadHandler` reads. */
export interface DownloadGridApi {
  getColumnState(): readonly SortColumnState[];
  getFilterModel(): Parameters<typeof parseFilterModel>[0];
}

/** For a ref whose `.current` already is the `GridApi` (e.g. an `onGridReady`-populated ref). */
export const apiFromGridApiRef = <T extends DownloadGridApi>(ref: T | null): T | null | undefined => ref;

/** For a ref whose `.current` is an `AgGridReact` wrapper — reads its `.api`. */
export const apiFromAgGridReactRef = <T extends DownloadGridApi>(ref: { api: T | null } | null): T | null | undefined =>
  ref?.api;

/**
 * Wires a "Download List" button's onPress: reads the grid's live sort/filter state, translates
 * it via the page's own `buildDownloadOptions`, and triggers the download mutation with a shared
 * error-toast path. Shared by the studies/samples/instrument-run download buttons — this glue was
 * previously copy-pasted at each call site.
 */
export function useCsvDownloadHandler<TGridRef, TOptions>(
  gridRef: RefObject<TGridRef>,
  extractApi: (ref: TGridRef | null) => DownloadGridApi | null | undefined,
  buildOptions: (
    columnState: readonly SortColumnState[],
    filterModel: Parameters<typeof parseFilterModel>[0],
  ) => TOptions,
  download: Pick<UseMutationResult<string, Error, TOptions>, 'mutate'>,
  errorMessage: string,
): () => void {
  return useCallback(() => {
    const api = extractApi(gridRef.current);
    const options = buildOptions(api?.getColumnState() ?? [], api?.getFilterModel() ?? {});
    download.mutate(options, { onError: () => toast.error(errorMessage) });
  }, [gridRef, extractApi, buildOptions, download, errorMessage]);
}
